import { type FC, useCallback, useState } from 'react'

import type { ScriptCategory } from '@api'

import { Error } from '@/components/ui/Error.tsx'
import { Button } from '@/components/ui/button.tsx'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { openDialog } from '@/models/dialogs.ts'
import { DefaultPlaceholderInserterPair } from '@/modules/message/components/editor/fields/default-placeholder-inserter-pair.tsx'
import { MessageFallbackSelectorController } from '@/modules/message/components/editor/fields/message-fallback-selector-controller.tsx'
import { MessageMediaSelectorController } from '@/modules/message/components/editor/fields/message-media-selector-controller.tsx'
import { MessageTextSelector } from '@/modules/message/components/editor/fields/message-text-selector.tsx'
import { MessageTitleSelectorController } from '@/modules/message/components/editor/fields/message-title-selector-controller.tsx'
import { useMessageEditor } from '@/modules/message/components/editor/message-editor-context.tsx'
import { MessageEditorForm } from '@/modules/message/components/editor/message-editor-form.tsx'
import { MessageEditor } from '@/modules/message/components/editor/message-editor.tsx'
import { CategorySelector } from '@/modules/scripts/components/CategorySelector.tsx'
import { classNames } from '@/utils/helpers.ts'

interface Props {
  mode: 'edit' | 'create'
  error?: unknown
  isSaving?: boolean
  onBack?: () => void
}

export const ScriptEditForm: FC<Props> = ({
  mode,
  error,
  isSaving,
  onBack,
}) => {
  const { isSubmitLoading } = useMessageEditor()
  const [newFolder, setNewFolder] = useState<ScriptCategory>()
  const handleCreateFolderClick = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.CREATE_SCRIPTS_FOLDER,
      onSuccessfulAdd: (folder) => {
        setNewFolder(folder)
      },
    })
  }, [])

  return (
    <MessageEditorForm addedCategoryId={newFolder?.id}>
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="mt-2">
          <h2 className="text-title text-[30px] font-semibold">
            {mode === 'edit' ? 'Edit' : 'New'} Script
          </h2>
          <p className="text-title max-w-[580px] text-sm">
            Use the button to insert the fan’s name, which will replace the
            label with their actual name. If the fan doesn’t have a name, the
            label will be replaced with the fallback name.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Button
            type="button"
            variant="outline"
            className="w-[132px]"
            onClick={onBack}
            testId="cancel-script-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className={classNames('flex items-center justify-center gap-2', {
              'w-[132px]': !isSaving,
              'pointer-events-none opacity-50': isSaving,
            })}
            isLoading={isSaving || isSubmitLoading}
            testId="save-script-button"
          >
            Save Script
          </Button>
        </div>
      </div>
      {!!error && (
        <Error
          className="mt-4"
          error={error}
        />
      )}
      <MessageEditor.Content className="bg-card mt-[24px] rounded-lg p-[24px]">
        <MessageTitleSelectorController className="col-span-12 max-w-[500px]" />
        <MessageTextSelector className="col-span-12" />
        <DefaultPlaceholderInserterPair className="col-span-12" />
        <MessageFallbackSelectorController
          className="col-span-12"
          inputClassName="w-full max-w-[500px]"
        />
        <MessageMediaSelectorController className="col-span-12" />
        {mode === 'create' && (
          <CategorySelector
            className="col-span-12 max-w-[500px]"
            handleCreateFolderClick={handleCreateFolderClick}
          />
        )}
      </MessageEditor.Content>
    </MessageEditorForm>
  )
}
